import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import api from '../services/api';

const NewsContext = createContext(null);

export const useNews = () => {
  const context = useContext(NewsContext);
  if (!context) {
    throw new Error('useNews must be used within NewsProvider');
  }
  return context;
};

export const NewsProvider = ({ children }) => {
  const { keycloak, initialized } = useKeycloak();
  const [news, setNews] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Prevent duplicate fetches
  const fetchedRef = useRef(false);

  /**
   * Fetch news from API
   */
  const fetchNews = useCallback(async (force = false) => {
    // Skip if not authenticated
    if (!keycloak?.authenticated) {
      return;
    }

    // Skip if already fetched and not forcing refresh
    if (fetchedRef.current && !force) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/news');
      setNews(response.data.news || []);
      setUnreadCount(response.data.unread_count || 0);
      setIsAdmin(response.data.is_admin || false);
      fetchedRef.current = true;
    } catch (err) {
      console.error('Failed to fetch news:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [keycloak?.authenticated]);

  /**
   * Mark a single news item as read
   */
  const markAsRead = useCallback(async (newsId) => {
    try {
      await api.post(`/news/${newsId}/read`);
      
      // Optimistic update
      setNews(prev => prev.map(item => 
        item.id === newsId ? { ...item, is_read: true } : item
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark news as read:', err);
    }
  }, []);

  /**
   * Mark all news as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await api.post('/news/mark-all-read');
      
      // Optimistic update
      setNews(prev => prev.map(item => ({ ...item, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all news as read:', err);
    }
  }, []);

  /**
   * Refresh news data
   */
  const refreshNews = useCallback(() => {
    fetchedRef.current = false;
    return fetchNews(true);
  }, [fetchNews]);

  // Fetch news when authenticated
  useEffect(() => {
    if (initialized && keycloak?.authenticated) {
      fetchNews();
    } else {
      // Reset state when logged out
      setNews([]);
      setUnreadCount(0);
      setIsAdmin(false);
      fetchedRef.current = false;
    }
  }, [initialized, keycloak?.authenticated, fetchNews]);

  const value = {
    news,
    unreadCount,
    isAdmin,
    loading,
    error,
    fetchNews,
    markAsRead,
    markAllAsRead,
    refreshNews,
  };

  return (
    <NewsContext.Provider value={value}>
      {children}
    </NewsContext.Provider>
  );
};

export default NewsContext;
